/** Pasta do SharePoint de uma pessoa/caso/processo (ou o espelho, no modo de emergência). */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Badge, Card, ErrorBox, Loading } from "./kit";
import { Icon } from "./icons";

export interface SpStatus { configured: boolean; mode: "normal" | "emergencia"; lastDeltaAt: string | null }
export function useSpStatus() {
  return useQuery({ queryKey: ["sharepoint-status"], queryFn: () => api.get<SpStatus>("/sharepoint/status"), staleTime: 60_000 });
}

interface FolderResp {
  mode: "normal" | "emergencia"; configured: boolean; mirrorAt?: string | null;
  folder: null | { id: string; name: string; path: string; webUrl: string | null; rootId: string; relPath: string };
  items: Array<{ id: string; name: string; isFolder: boolean; size: number; webUrl: string | null; modifiedAt: string | null; mirrored?: boolean }>;
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function SharePointFolder({ ctx }: { ctx: { personId?: string; caseId?: string; processId?: string } }): JSX.Element | null {
  const { data: st } = useSpStatus();
  const qc = useQueryClient();
  const [folderId, setFolderId] = useState<string | null>(null);
  const [trail, setTrail] = useState<Array<{ id: string; name: string }>>([]);
  const [creating, setCreating] = useState(false);
  const key = ["sharepoint-pasta", ctx, folderId];
  const { data, error, isLoading } = useQuery({
    queryKey: key, enabled: !!st && (st.configured || st.mode === "emergencia"), retry: false,
    queryFn: () => api.get<FolderResp>(`/sharepoint/pasta${qs({ ...ctx, folderId: folderId ?? undefined })}`),
  });
  if (!st || (!st.configured && st.mode !== "emergencia")) return null;

  const create = async () => {
    setCreating(true);
    try {
      await api.get(`/sharepoint/pasta${qs({ ...ctx, criar: "1" })}`);
      await qc.invalidateQueries({ queryKey: ["sharepoint-pasta"] });
    } finally { setCreating(false); }
  };
  const open = (id: string, name: string) => { setTrail([...trail, { id, name }]); setFolderId(id); };
  const goTo = (i: number) => { const t = trail.slice(0, i + 1); setTrail(t); setFolderId(i < 0 ? null : t[t.length - 1]!.id); };
  const emergency = data?.mode === "emergencia";

  return (
    <Card title={<>Pasta no SharePoint {emergency && <Badge tone="warn">modo de emergência</Badge>}</>} actions={
      data?.folder?.webUrl && !emergency ? <a className="btn small" href={data.folder.webUrl} target="_blank" rel="noreferrer"><Icon name="ext" size="sm" /> Abrir no SharePoint</a> : null
    }>
      {emergency && <div className="warn-box" style={{ marginBottom: 8 }}>O SharePoint está em modo de emergência: você está vendo a cópia do espelho{data?.mirrorAt ? ` de ${fmtDateTime(data.mirrorAt)}` : ""}. Envios novos ficam guardados e sobem quando o modo normal voltar.</div>}
      <ErrorBox error={error} />
      {isLoading ? <Loading /> : !data?.folder ? (
        emergency ? <div className="muted small">Esta pasta ainda não tinha sido espelhada.</div> : (
          <div className="row"><span className="muted small">Ainda não há pasta para este {ctx.processId ? "processo" : ctx.caseId ? "caso" : "cliente"}.</span>
            <button className="btn small primary" disabled={creating} onClick={() => void create()}>{creating ? "Criando…" : "Criar pasta"}</button></div>
        )
      ) : (
        <>
          <div className="row small" style={{ gap: 4, marginBottom: 6 }}>
            <button className="link-btn" onClick={() => goTo(-1)}>{data.folder.relPath ? "Pasta principal" : data.folder.name}</button>
            {trail.map((t, i) => <span key={t.id}> / <button className="link-btn" onClick={() => goTo(i)}>{t.name}</button></span>)}
          </div>
          {data.items.map((it) => (
            <div key={it.id} className="lrow" onClick={() => (it.isFolder ? open(it.id, it.name) : undefined)} style={{ cursor: it.isFolder ? "pointer" : "default" }}>
              <Icon name={it.isFolder ? "folder" : "doc"} />
              <div className="grow"><div className="tt">{it.name}</div><div className="mm">{it.isFolder ? "pasta" : fmtSize(it.size)}{it.modifiedAt ? ` · ${fmtDateTime(it.modifiedAt)}` : ""}</div></div>
              {!it.isFolder && (
                <>
                  {it.webUrl && !emergency && <a className="btn small" href={it.webUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>Abrir</a>}
                  {(!emergency || it.mirrored) && <button className="btn small" onClick={(e) => { e.stopPropagation(); void api.download(`/sharepoint/arquivo/${it.id}${qs(ctx)}`, it.name); }}>Baixar</button>}
                </>
              )}
            </div>
          ))}
          {!data.items.length && <div className="muted small">Pasta vazia.</div>}
        </>
      )}
    </Card>
  );
}

export function SyncBadge({ status, webUrl, error }: { status: string; webUrl?: string | null; error?: string | null }): JSX.Element | null {
  const { data: st } = useSpStatus();
  if (!st?.configured) return null;
  if (status === "ok") return webUrl ? <a href={webUrl} target="_blank" rel="noreferrer" title="Abrir no SharePoint"><Badge tone="ok">no SharePoint</Badge></a> : <Badge tone="ok">no SharePoint</Badge>;
  if (status === "erro") return <span title={error ?? ""}><Badge tone="bad">erro no envio</Badge></span>;
  return <Badge tone="muted">{st.mode === "emergencia" ? "aguardando (emergência)" : "enviando…"}</Badge>;
}
